import KroAmountInputField from "features/components/inputs/KroAmountInput";
import { formatCurrency } from "common/utils/currencyFormater";
import { FC } from "react";
import { KroButton } from "features/components/buttons/kroButton";
import { CampaignModelMapper } from "features/campaign/domain/models/campaignModel";
import { useKroFee } from "features/campaign/presentation/hooks/useKroFee";
import { calculateProvidusAmount } from "common/utils/fee_calculator";
import { useForm } from "@tanstack/react-form";
import { validateWithdrawalAmount } from "common/utils/validateWithdrawalAmount";
import { toast } from "react-toastify";
import { WithdrawalDetails } from "features/dashboard/domain/model/withdrawalDetails";
import { WithdrawalType } from "features/dashboard/domain/enums/withdrawalType.enum";

const WithdrawalOverview: FC<{
  campaign: CampaignModelMapper;
  onClose?: () => void;
  withdrawalType?: string;
  closeWithdrawalOptions?: () => void;
  openDepositAccount?: (details: WithdrawalDetails) => void;
}> = ({
  withdrawalType,
  campaign,
  closeWithdrawalOptions,
  openDepositAccount,
}) => {
    const {
      error: errorFetchingKroFee,
      isLoading: isLoadingKroFee,
      data: kroFee,
      refetch,
    } = useKroFee();

    const form = useForm({
      defaultValues: {
        amount:
          withdrawalType === WithdrawalType.complete
            ? campaign.balance.toString()
            : "",
      },
    });

    const amountValue = form.useStore((state) => state.values.amount);
    const amount = Number.parseInt(amountValue);

    const { fee: bankCharges } = calculateProvidusAmount(amount);
    const withdrawalFee = kroFee?.percentage
      ? (amount * kroFee.percentage) / 100
      : 0;
    const total = amount - withdrawalFee - bankCharges;
    const totalFee = withdrawalFee + bankCharges;

    const amountWithdrawable =
      campaign?.withdrawableBalance === 0
        ? campaign.amountRaised * campaign.withdrawalCapPercentage
        : campaign?.withdrawableBalance;

    const withdrawalFeePercentage = kroFee?.percentage?.toString();

    const handleRequestPayout = () => {
      if (isNaN(amount)) return;

      const withdrawalDetails: WithdrawalDetails = {
        amount,
        totalFee,
        withdrawalType,
      };

      closeWithdrawalOptions?.();
      openDepositAccount?.(withdrawalDetails);
    };

    if (errorFetchingKroFee) {
      toast.error(
        ({ closeToast }) => (
          <div className="flex items-center justify-between gap-2">
            <span>{errorFetchingKroFee?.message ?? ""}</span>
            <span
              onClick={() => {
                refetch();
                closeToast();
              }}
              className="text-sm cursor-pointer text-black underline hover:text-gray-700"
            >
              Reload
            </span>
          </div>
        ),
        {
          autoClose: false,
          closeOnClick: false,
        },
      );
    }
    return (
      <div className="mt-5">
        {campaign?.status !== "ended" && (
          <div className="flex items-center bg-[#FFFBED] p-3 px-[40px]">
            <p className="font-inter text-[14px] text-[#7E4510]">
              {withdrawalType === WithdrawalType.partial
                && `You can withdraw up to ${formatCurrency(amountWithdrawable, true)}`
              }
            </p>
          </div>
        )}
        <div className="mx-5 mt-6 flex flex-col">
          <form.Field
            name="amount"
            validators={{
              onChange: ({ value }) =>
                validateWithdrawalAmount({
                  value,
                  withdrawalType,
                  campaign,
                  amountWithdrawable,
                }),
            }}
            children={(field) => (
              <>
                <KroAmountInputField
                  currency={"₦"}
                  placeHolder=""
                  id={field.name}
                  value={field.state.value}
                  name={field.name}
                  className="rounded-[20px] border border-neutral-200 bg-neutral-50 px-3"
                  onChange={(value) => {
                    if (campaign?.status !== "ended") {
                      field.handleChange(value);
                    }
                  }}
                  title="You're Withdrawing"
                  readOnly={withdrawalType !== WithdrawalType.partial}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm mt-2 text-red-500">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </>
            )}
          />
          <div className="mt-2 flex justify-center self-end rounded-[32px] bg-[#F0F9F3] p-3 lg:min-w-[274px]">
            <p className="font-inter text-[14px] font-semibold text-[#276338]">
              Campaign Funds Remaining: {formatCurrency(campaign?.balance, true)}
            </p>
          </div>
          <div className="mt-6 border-t border-stroke-soft-200" />

          <h2 className="mt-6 font-inter text-[18px] font-medium text-neutral-950">
            Overview
          </h2>

          {/* Withdrawal Amount */}
          <div className="mt-4 flex justify-between">
            <h2 className="font-inter text-body-lg font-medium text-neutral-900">
              Your withdrawal
            </h2>
            <h2 className="font-inter text-body-lg font-medium text-neutral-900">
              {formatCurrency(isNaN(amount) ? "0" : amountValue)}
            </h2>
          </div>

          {/* Giv Charges */}
          <div className="mt-4 flex justify-between">
            <h2 className="font-inter text-body-lg font-medium text-neutral-900">
              Giv Charges ({withdrawalFeePercentage}%)
            </h2>
            <h2 className="font-inter text-body-lg font-medium text-neutral-900">
              {isLoadingKroFee ? (
                <span className="inline-block h-5 w-12 animate-pulse rounded bg-neutral-200" />
              ) : (
                (formatCurrency(withdrawalFee.toFixed(2)) ?? "0")
              )}
            </h2>
          </div>

          {/* Bank Charges */}
          <div className="mt-4 flex justify-between">
            <h2 className="font-inter text-body-lg font-medium text-neutral-900">
              Bank Charges
            </h2>
            <h2 className="font-inter text-body-lg font-medium text-neutral-900">
              {isLoadingKroFee ? (
                <span className="inline-block h-5 w-12 animate-pulse rounded bg-neutral-200" />
              ) : Number.parseInt(amountValue) < 1 ? (
                formatCurrency(0)
              ) : (
                formatCurrency(bankCharges)
              )}
            </h2>
          </div>

          {/* Total */}
          <div className="mb-5 mt-5 border-t border-stroke-soft-200">
            <div className="mt-4 flex justify-between">
              <span className="text-body-xl font-[500] text-[#0F0F0F]">
                Total
              </span>
              <span className="text-body-xl font-[500] text-[#0F0F0F]">
                {isLoadingKroFee ? (
                  <span className="inline-block h-6 w-16 animate-pulse rounded bg-neutral-200" />
                ) : (
                  formatCurrency(total.toFixed(2))
                )}
              </span>
            </div>
          </div>

          <KroButton
            title="Request Payout"
            isDisabled={
              !amount ||
              (withdrawalType === WithdrawalType.partial
                ? amount > (amountWithdrawable || 0)
                : withdrawalType === "Complete"
                  ? amount > (campaign?.balance || 0)
                  : false)
            }
            onclick={handleRequestPayout}
            className="mb-5 h-[60px] w-full"
          />
        </div>
      </div>
    );
  };

export default WithdrawalOverview;
